import React, { useEffect, useRef } from 'react';

declare global {
  interface Window {
    VANTA?: {
      FOG?: (config: Record<string, unknown>) => { destroy?: () => void };
      WAVES?: (config: Record<string, unknown>) => { destroy?: () => void };
      NET?: (config: Record<string, unknown>) => { destroy?: () => void };
    };
    THREE?: unknown;
  }
}

type DynamicBackgroundEffect = 'fog' | 'waves' | 'net';

interface BackgroundProps {
  children: React.ReactNode;
  variant?: 'static' | 'dynamic';
  dynamicEffect?: DynamicBackgroundEffect;
}

const loadedScripts = new Map<string, Promise<void>>();

const loadScript = (src: string) => {
  if (loadedScripts.has(src)) {
    return loadedScripts.get(src)!;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existingScript) {
      if (existingScript.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.loaded = 'false';
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', reject);
    document.body.appendChild(script);
  });

  loadedScripts.set(src, promise);
  return promise;
};

const StaticGradientLayer: React.FC = () => (
  <div className="fixed inset-0 bg-gradient-to-b from-[#0B0B28] to-[#1B254F]">
    <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_60%_50%,rgba(163,47,128,0.25),transparent_80%)] mix-blend-screen" />
    <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_40%_70%,rgba(108,58,178,0.2),transparent_80%)] mix-blend-screen" />
    <div className="absolute inset-0 bg-[radial-gradient(100%_80%_at_50%_50%,rgba(76,194,241,0.1),transparent_100%)] mix-blend-screen" />
  </div>
);

const THREE_SRC = 'https://cdn.jsdelivr.net/npm/three@0.134.0/build/three.min.js';

const VANTA_EFFECTS: Record<
  DynamicBackgroundEffect,
  {
    script: string;
    init: (element: HTMLElement) => { destroy?: () => void } | undefined;
  }
> = {
  fog: {
    script: 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.fog.min.js',
    init: (element) =>
      window.VANTA?.FOG?.({
        el: element,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 200.0,
        minWidth: 200.0,
        highlightColor: 0xd200ff,
        midtoneColor: 0x67078e,
        lowlightColor: 0x570da2,
        baseColor: 0x7e0aaf,
        blurFactor: 0.87,
        speed: 2.6,
        zoom: 0.3,
      }),
  },
  waves: {
    script: 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.waves.min.js',
    init: (element) =>
      window.VANTA?.WAVES?.({
        el: element,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 200.0,
        minWidth: 200.0,
        shininess: 45.0,
        waveHeight: 18.0,
        waveSpeed: 0.75,
        zoom: 0.9,
        color: 0x4b1d79,
        waveColor: 0x8f4bc6,
        backgroundColor: 0x06061f,
      }),
  },
  net: {
    script: 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.net.min.js',
    init: (element) =>
      window.VANTA?.NET?.({
        el: element,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 200.0,
        minWidth: 200.0,
        points: 12.0,
        maxDistance: 22.0,
        spacing: 18.0,
        color: 0x7c3aed,
        backgroundColor: 0x06061f,
      }),
  },
};

const DynamicVantaLayer: React.FC<{ effect: DynamicBackgroundEffect }> = ({ effect }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const effectRef = useRef<{ destroy?: () => void } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current) {
      return;
    }

    let cancelled = false;

    const initVanta = async () => {
      try {
        const config = VANTA_EFFECTS[effect];
        await loadScript(THREE_SRC);
        await loadScript(config.script);

        if (cancelled || !containerRef.current) {
          return;
        }

        if (effectRef.current?.destroy) {
          effectRef.current.destroy();
        }

        effectRef.current = config.init(containerRef.current) ?? null;
      } catch (error) {
        console.error('Failed to initialize Vanta background:', error);
      }
    };

    initVanta();

    return () => {
      cancelled = true;
      if (effectRef.current?.destroy) {
        effectRef.current.destroy();
      }
      effectRef.current = null;
    };
  }, [effect]);

  return <div ref={containerRef} className="fixed inset-0" />;
};

export const Background: React.FC<BackgroundProps> = ({ children, variant = 'static', dynamicEffect = 'fog' }) => {
  return (
    <div className="relative min-h-screen bg-[#0B0B28]">
      {variant === 'dynamic' ? (
        <>
          <DynamicVantaLayer effect={dynamicEffect} />
          <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/35" />
        </>
      ) : (
        <StaticGradientLayer />
      )}

      <div className="relative z-10">{children}</div>
    </div>
  );
};
